
import * as React from 'react';
import { useState, useEffect } from 'react';
import {
    Box,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    Typography,
    Divider,
    Avatar
} from '@mui/material';
import {
    Dashboard,
    Receipt,
    Business,
    FlightTakeoff,
    Settings,
    ExpandLess,
    ExpandMore,
    AccountBalance,
    Assessment,
    Payment,
    Hotel,
    DirectionsCar,
    Tour,
    Calculate,
    CalendarMonth,
    MenuBook,
    People,
    LocationOn,
    SyncAlt
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { COLORS } from '../../settings/themeSettings';
import './Sidebar.scss';

function Sidebar() {
    const navigate = useNavigate();
    const location = useLocation();
    const { me } = useSelector((state) => state.me);

    // État pour gérer l'ouverture/fermeture des sections
    const [openSections, setOpenSections] = useState({
        etat: true,
        production: false,
        billetterie: false,
        facturation: false,
        utilitaires: false
    });

    // Fonction pour vérifier si un chemin est actif
    const isActive = (path) => {
        return location.pathname === path;
    };

    // Fonction pour vérifier si une section contient le chemin actif
    const isSectionActive = (items) => {
        return items.some(item => isActive(item.path));
    };

    // Ouvrir automatiquement la section qui contient la page active
    useEffect(() => {
        const currentPath = location.pathname;

        if (currentPath.startsWith('/dashboard') || currentPath.startsWith('/etat/')) {
            setOpenSections(prev => ({ ...prev, etat: true }));
        } else if (currentPath.startsWith('/dossiers/')) {
            setOpenSections(prev => ({ ...prev, production: true }));
        } else if (currentPath.startsWith('/billeterie/')) {
            setOpenSections(prev => ({ ...prev, billetterie: true }));
        } else if (currentPath.startsWith('/facturation/')) {
            setOpenSections(prev => ({ ...prev, facturation: true }));
        } else if (currentPath.startsWith('/utilitaires/') || currentPath.startsWith('/users') ||
            currentPath.startsWith('/chauffeurs') || currentPath.startsWith('/vehicules') ||
            currentPath.startsWith('/agences') || currentPath.startsWith('/hotels') ||
            currentPath.startsWith('/type-frais') || currentPath.startsWith('/programmes') ||
            currentPath.startsWith('/toclients') || currentPath.startsWith('/calendars')) {
            setOpenSections(prev => ({ ...prev, utilitaires: true }));
        }
    }, [location.pathname]);

    const toggleSection = (section) => {
        setOpenSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const profil = me?.profil || 'USER';
    const isGeneralAdmin = profil === 'ADMIN';
    const isAdminProd = profil === 'ADMIN-PRODUCTION' || profil === 'PRODUCTION';
    const isAdminBill = profil === 'ADMIN-BILLETTERIE';
    const isAdminCompta = profil === 'ADMIN-COMPTABILITE';

    const menuSections = React.useMemo(() => {
        const sections = [];

        if (isGeneralAdmin) {
            sections.push({
                id: 'etat',
                title: 'État',
                icon: <Assessment />,
                items: [
                    { title: 'Tableaux de bord', icon: <Dashboard />, path: '/etat/dashboard' },
                    { title: 'Statistiques', icon: <Assessment />, path: '/etat/statistiques' },
                    { title: 'Journal des ventes', icon: <MenuBook />, path: '/etat/journal-ventes' },
                    { title: 'Journal de caisse', icon: <AccountBalance />, path: '/etat/journal-caisse' },
                    { title: 'Journal des paiements', icon: <Payment />, path: '/etat/journal-paiements' },
                    { title: 'Journal de facturation', icon: <Receipt />, path: '/etat/journal-facturation' },
                    { title: 'Liste clients', icon: <People />, path: '/etat/liste-clients' },
                    { title: 'Journal des événements', icon: <MenuBook />, path: '/etat/journal-evenements' }
                ]
            });
        }

        if (isGeneralAdmin || isAdminProd || isAdminCompta) {
            sections.push({
                id: 'production',
                title: 'Production',
                icon: <Tour />,
                items: [
                    { title: 'Circuits', icon: <Tour />, path: '/dossiers/circuit' },
                    { title: 'Séjours', icon: <Hotel />, path: '/dossiers/sejour' },
                    { title: 'Excursions', icon: <LocationOn />, path: '/dossiers/excursion' },
                    { title: 'Transferts', icon: <DirectionsCar />, path: '/dossiers/transfert' }
                ]
            });
        }

        if (isGeneralAdmin || isAdminBill) {
            sections.push({
                id: 'billetterie',
                title: 'Billetterie',
                icon: <FlightTakeoff />,
                items: [
                    { title: 'Gestion Billets', icon: <FlightTakeoff />, path: '/billeterie/portefeuil' }
                ]
            });
        }

        if (isGeneralAdmin || isAdminCompta) {
            sections.push({
                id: 'facturation',
                title: 'Facturation',
                icon: <Receipt />,
                items: [
                    { title: 'Gestion client', icon: <People />, path: '/facturation/gestion-client' },
                    { title: 'Saisie encaissements', icon: <Payment />, path: '/facturation/encaissements' },
                    { title: 'Compte client', icon: <MenuBook />, path: '/facturation/compte-client' }
                ]
            });

            sections.push({
                id: 'sage',
                title: 'Interface SAGE',
                icon: <SyncAlt />,
                items: [
                    { title: 'Paramétrage', icon: <Settings />, path: '/sage/parametres' },
                    { title: 'Transfert des écritures', icon: <SyncAlt />, path: '/sage/transfert' },
                    { title: 'Éditions', icon: <MenuBook />, path: '/sage/editions' }
                ]
            });
        }

        if (isGeneralAdmin || isAdminProd || isAdminBill || isAdminCompta) {
            sections.push({
                id: 'utilitaires',
                title: 'Utilitaires',
                icon: <Settings />,
                items: [
                    ...(isGeneralAdmin ? [{ title: 'Utilisateurs', icon: <People />, path: '/users' }] : []),
                    { title: 'Paramètres société', icon: <Settings />, path: '/utilitaires/parametres-societe' },
                    // { title: 'Codes Clients & Tiers', icon: <People />, path: '/utilitaires/codes-clients' },
                    { title: "Gestion des types d'activité", icon: <Business />, path: '/utilitaires/types-activite' },
                    { title: 'Plan tiers', icon: <People />, path: '/utilitaires/plan-tiers' },
                    { title: 'Interface SAGE', icon: <Settings />, path: '/utilitaires/sage' },
                    { title: 'Chauffeurs', icon: <DirectionsCar />, path: '/chauffeurs' },
                    { title: 'Véhicules', icon: <DirectionsCar />, path: '/vehicules' },
                    { title: 'Guides', icon: <People />, path: '/guides' },
                    { title: 'Agences', icon: <Business />, path: '/agences' },
                    { title: 'Hôtels', icon: <Hotel />, path: '/hotels' },
                    { title: 'TO Clients', icon: <People />, path: '/toclients' },
                    { title: 'Types de frais', icon: <Receipt />, path: '/type-frais' },
                    { title: 'Programmes', icon: <Tour />, path: '/programmes' },
                    { title: 'Planning Logistique', icon: <CalendarMonth />, path: '/calendars' },
                    { title: 'Calendrier Transferts', icon: <DirectionsCar />, path: '/transfert-calendar' }
                ]
            });
        }

        return sections;
    }, [isGeneralAdmin, isAdminProd, isAdminBill, isAdminCompta]);

    const quickActions = React.useMemo(() => {
        const actions = [];
        if (isGeneralAdmin || isAdminCompta) {
            actions.push({ title: 'Cotations', icon: <Assessment />, path: '/cotations' });
        }
        if (isGeneralAdmin || isAdminProd || isAdminBill || isAdminCompta) {
            actions.push({ title: 'Planning', icon: <CalendarMonth />, path: '/calendars' });
            actions.push({ title: 'Calculatrice', icon: <Calculate />, path: '/calculatrice' });
        }
        if (isGeneralAdmin) {
            actions.push({ title: 'Documentation', icon: <MenuBook />, path: '/documentation' });
        }
        return actions;
    }, [isGeneralAdmin, isAdminProd, isAdminBill, isAdminCompta]);

    // Navigation au clavier
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const flatMenu = React.useMemo(() => {
        const items = [];
        menuSections.forEach(section => {
            items.push({ type: 'section', ...section });
            if (openSections[section.id]) {
                section.items.forEach(item => {
                    items.push({ type: 'item', parentId: section.id, ...item });
                });
            }
        });
        quickActions.forEach(action => {
            items.push({ type: 'action', ...action });
        });
        return items;
    }, [menuSections, openSections, quickActions]);

    const handleSidebarKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedIndex(prev => (prev < flatMenu.length - 1 ? prev + 1 : 0));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIndex(prev => (prev > 0 ? prev - 1 : flatMenu.length - 1));
        } else if (e.key === 'Enter' && focusedIndex !== -1) {
            e.preventDefault();
            const item = flatMenu[focusedIndex];
            if (item) {
                if (item.type === 'section') {
                    toggleSection(item.id);
                } else {
                    navigate(item.path);
                }
            }
        }
    };

    return (
        <Box className="professional-sidebar" tabIndex={-1} onKeyDown={handleSidebarKeyDown}>
            {/* Header simple */}
            <Box className="sidebar-header">
                <Avatar
                    sx={{
                        width: 40,
                        height: 40,
                        bgcolor: COLORS.primary,
                        fontSize: '1rem',
                        fontWeight: 600
                    }}
                >
                    {me?.nom?.charAt(0) || 'U'}
                </Avatar>
                <Box sx={{ ml: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                        {me?.nom || 'Utilisateur'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                        {me?.profil || 'USER'}
                    </Typography>
                </Box>
            </Box>

            <Divider />

            {/* Menu principal */}
            <List sx={{
                flex: 1,
                py: 1,
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                    width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#c1c1c1',
                    borderRadius: '3px',
                    '&:hover': {
                        backgroundColor: '#a8a8a8',
                    },
                },
            }}>
                {menuSections.map((section, sIdx) => {
                    const sectionIndex = flatMenu.findIndex(i => i.id === section.id && i.type === 'section');
                    return (
                        <React.Fragment key={section.id}>
                            <ListItemButton
                                onClick={() => toggleSection(section.id)}
                                className={`${isSectionActive(section.items) ? 'section-active' : ''} ${focusedIndex === sectionIndex ? 'focused' : ''}`}
                                sx={{
                                    mx: 1,
                                    borderRadius: 1,
                                    mb: 0.5,
                                    '&.focused': { borderLeft: `4px solid ${COLORS.primary}`, bgcolor: '#f0f0f0' },
                                    '&:hover': { bgcolor: '#f5f5f5' }
                                }}
                            >
                                <ListItemIcon sx={{ color: '#555', minWidth: 36 }}>
                                    {section.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={section.title}
                                    primaryTypographyProps={{
                                        fontWeight: 500,
                                        fontSize: '0.9rem',
                                        color: '#333'
                                    }}
                                />
                                {openSections[section.id] ?
                                    <ExpandLess sx={{ color: '#666' }} /> :
                                    <ExpandMore sx={{ color: '#666' }} />
                                }
                            </ListItemButton>

                            <Collapse in={openSections[section.id]} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {section.items.map((item, iIdx) => {
                                        const itemIndex = flatMenu.findIndex(i => i.path === item.path && i.type === 'item');
                                        return (
                                            <ListItemButton
                                                key={`${item.path}-${item.title}`}
                                                onClick={() => navigate(item.path)}
                                                className={`${isActive(item.path) ? 'item-active' : ''} ${focusedIndex === itemIndex ? 'focused' : ''}`}
                                                sx={{
                                                    pl: 4,
                                                    mx: 1,
                                                    borderRadius: 1,
                                                    mb: 0.25,
                                                    py: 0.75,
                                                    '&.focused': { borderLeft: `4px solid ${COLORS.primary}`, bgcolor: '#f0f0f0' },
                                                    '&:hover': { bgcolor: '#f8f9fa' }
                                                }}
                                            >
                                                <ListItemIcon sx={{ color: '#666', minWidth: 32 }}>
                                                    {item.icon}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={item.title}
                                                    primaryTypographyProps={{
                                                        fontSize: '0.85rem',
                                                        color: isActive(item.path) ? COLORS.primary : '#555'
                                                    }}
                                                />
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            </Collapse>
                        </React.Fragment>
                    );
                })}
            </List>

            <Divider />

            {/* Actions rapides */}
            <Box sx={{ p: 1 }}>
                <Typography variant="overline" sx={{
                    color: '#888',
                    fontWeight: 600,
                    px: 1,
                    fontSize: '0.7rem'
                }}>
                    Outils
                </Typography>
                <List dense>
                    {quickActions.map((action, aIdx) => {
                        const actionIndex = flatMenu.findIndex(i => i.path === action.path && i.type === 'action');
                        return (
                            <ListItemButton
                                key={`${action.path}-${action.title}`}
                                onClick={() => navigate(action.path)}
                                className={`${isActive(action.path) ? 'item-active' : ''} ${focusedIndex === actionIndex ? 'focused' : ''}`}
                                sx={{
                                    borderRadius: 1,
                                    mb: 0.25,
                                    py: 0.5,
                                    '&.focused': { borderLeft: `4px solid ${COLORS.primary}`, bgcolor: '#f0f0f0' },
                                    '&:hover': { bgcolor: '#f8f9fa' }
                                }}
                            >
                                <ListItemIcon sx={{ color: '#666', minWidth: 32 }}>
                                    {action.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={action.title}
                                    primaryTypographyProps={{
                                        fontSize: '0.8rem',
                                        color: isActive(action.path) ? COLORS.primary : '#555'
                                    }}
                                />
                            </ListItemButton>
                        );
                    })}
                </List>
            </Box>
        </Box>
    );
}

export default Sidebar;
